import { ball, body, coverBg, edge, fade, gloss, mix, n, shade, star, tint, type ArtPal } from './kit';

/** koi silhouette (head left at x −46, body to x 34) — same outline as the koi symbol */
const KOI_BODY =
  'M-46 2C-46 -12 -32 -20 -12 -20C8 -20 22 -12 32 -4C34 -1 34 3 32 5C22 13 8 18 -12 18C-32 18 -46 14 -46 2Z';

/**
 * Koi Pond: a night pond (the left third stays calm for labels, every focus object sits right of x 213) — full moon with a halo, a red moon bridge whose reflection closes the circle,
 * a golden and a red koi circling each other, lily pads and two pink lotuses, three red lanterns.
 */
export function koiPondCover(P: ArtPal, p: string): string {
  const top = P.dark ? mix(P.bg, P.blue, 0.24) : mix(P.purple, '#FFFFFF', 0.76);
  const mid = P.dark ? mix(P.panel, P.teal, 0.12) : mix(P.pink, '#FFFFFF', 0.82);
  const bottom = P.dark ? mix(P.bg, P.teal, 0.12) : mix(P.teal, '#FFFFFF', 0.7);
  const bg = coverBg(p, P, top, mid, bottom, [480, 130]);
  const water = P.dark ? mix(P.bg, P.teal, 0.24) : mix(P.teal, '#FFFFFF', 0.58);
  const moon = P.dark ? '#FFF1C2' : '#FFE7A3';
  /** sunny koi gold (as on the koi symbol): the light-theme gold reads brown on big shapes */
  const gold = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.72);
  const fin = P.dark ? P.orange : tint(P.orange, 0.1);
  const shore = P.dark ? mix(P.bg, P.teal, 0.08) : mix(P.teal, P.purple, 0.5);
  const white = (o: number, lo: number) => `stroke="#FFFFFF" stroke-opacity="${P.dark ? o : lo}"`;

  /** a koi swimming left (`s` < 0 mirrors it to swim right) */
  const koi = (cx: number, cy: number, rot: number, s: number, c: string, patch: string, k: string) =>
    `<g transform="translate(${cx} ${cy}) rotate(${rot}) scale(${s} ${Math.abs(s)})">` +
    `<ellipse cx="-6" cy="30" rx="40" ry="6" fill="${shade(water, 0.35)}" fill-opacity=".35"/>` +
    `<path d="M28 -2C38 -16 50 -28 62 -30C57 -18 55 -8 51 0C55 8 58 20 63 30C50 28 38 16 28 4Z" fill="url(#${p}fn)" stroke="${edge(fin, P)}" stroke-width="1.8" stroke-linejoin="round"/>` +
    `<path d="M-16 -18C-10 -34 10 -32 20 -13Z" fill="url(#${p}fn)" stroke="${edge(fin, P)}" stroke-width="1.8" stroke-linejoin="round"/>` +
    `<path d="${KOI_BODY}" fill="url(#${p}${k})" stroke="${edge(c, P)}" stroke-width="2.2" stroke-linejoin="round"/>` +
    `<g clip-path="url(#${p}c${k})" fill="${patch}"><ellipse cx="-24" cy="-16" rx="12" ry="8"/><ellipse cx="8" cy="-14" rx="11" ry="9"/><ellipse cx="22" cy="10" rx="7" ry="6"/></g>` +
    `<path d="M-26 10C-30 22 -26 30 -16 32C-16 24 -16 18 -18 11Z" fill="url(#${p}fn)" stroke="${edge(fin, P)}" stroke-width="1.6"/>` +
    `<path d="M-45 4q-7 3 -9 11" fill="none" stroke="${edge(c, P)}" stroke-width="1.6" stroke-linecap="round"/>` +
    `<circle cx="-35" cy="-5" r="3.6" fill="#FFFFFF"/><circle cx="-35.8" cy="-4.8" r="2.2" fill="#141A26"/>` +
    gloss(-18, -12, 16, 3.5, -4, 0.55) +
    `</g>`;

  const lantern = (x: number, y: number, s: number) =>
    `<g transform="translate(${x} ${y}) scale(${s})">` +
    `<circle cx="0" cy="0" r="54" fill="url(#${p}lh)"/>` +
    `<path d="M0 -70V-32" stroke="${P.dark ? tint(P.ink, 0.2) : shade(P.ink, 0.1)}" stroke-opacity=".55" stroke-width="2"/>` +
    `<rect x="-13" y="-33" width="26" height="8" rx="3" fill="url(#${p}gd)" stroke="${edge(gold, P)}" stroke-width="1.4"/>` +
    `<ellipse cx="0" cy="0" rx="28" ry="26" fill="url(#${p}ln)" stroke="${edge(P.red, P)}" stroke-width="2"/>` +
    `<path d="M0 -25C-11 -16 -11 16 0 25M0 -25C11 -16 11 16 0 25M0 -25C-22 -16 -22 16 0 25M0 -25C22 -16 22 16 0 25" fill="none" stroke="${shade(P.red, 0.3)}" stroke-width="1.3" stroke-opacity=".5"/>` +
    `<rect x="-13" y="25" width="26" height="8" rx="3" fill="url(#${p}gd)" stroke="${edge(gold, P)}" stroke-width="1.4"/>` +
    `<path d="M0 33V50M-4 35L-6 50M4 35L6 50" stroke="${gold}" stroke-width="2.2" stroke-linecap="round"/>` +
    gloss(-10, -10, 8, 4.5, -35, 0.5) +
    `</g>`;

  /** lily pad: an ellipse with a notch towards the viewer */
  const pad = (cx: number, cy: number, rx: number, rot: number) => {
    const ry = rx * 0.32;
    const at = (a: number) => `${n(cx + rx * Math.cos(a))} ${n(cy + ry * Math.sin(a))}`;
    return `<path d="M${cx} ${cy}L${at(1.3)}A${n(rx)} ${n(ry)} 0 1 0 ${at(1.75)}Z" transform="rotate(${rot} ${cx} ${cy})" fill="url(#${p}pd)" stroke="${edge(P.green, P)}" stroke-width="1.6" stroke-linejoin="round"/>`;
  };

  const flower = (cx: number, cy: number, s: number) =>
    `<g transform="translate(${cx} ${cy}) scale(${s})">` +
    [-62, -32, 32, 62, -12, 12, 0]
      .map(
        (a) =>
          `<path d="M0 0C-7 -10 -6 -24 0 -32C6 -24 7 -10 0 0Z" transform="rotate(${a})" fill="url(#${p}${Math.abs(a) < 20 ? 'lc' : 'lo'})" stroke="${edge(P.pink, P)}" stroke-width="1.3"/>`,
      )
      .join('') +
    `<circle cx="0" cy="-5" r="4" fill="${gold}" stroke="${edge(gold, P)}" stroke-width="1"/>` +
    `</g>`;

  const stars = P.dark
    ? [
        [80, 40, 3],
        [170, 90, 2.2],
        [360, 30, 2.6],
        [600, 40, 2.4],
        [300, 20, 1.8],
      ]
        .map(
          ([x, y, r]) =>
            `<polygon points="${star(x as number, y as number, r as number, (r as number) * 0.35, 4)}" fill="${moon}" fill-opacity=".8"/>`,
        )
        .join('')
    : '';

  const defs =
    `<defs>${bg.defs}` +
    ball(`${p}mo`, moon, 0.6, 0.18) +
    fade(`${p}mh`, moon, P.dark ? 0.5 : 0.85) +
    body(`${p}wt`, water, 0.15, 0.3, 0, 1) +
    body(`${p}br`, P.red, 0.4, 0.35, 0.3, 1) +
    body(`${p}kg`, gold, 0.55, 0.28, 0.2, 1) +
    body(`${p}kr`, P.red, 0.4, 0.35, 0.2, 1) +
    body(`${p}fn`, fin, 0.45, 0.3, 0.6, 1) +
    body(`${p}gd`, gold, 0.5, 0.35, 1, 0) +
    ball(`${p}ln`, P.red, 0.5, 0.4) +
    fade(`${p}lh`, P.orange, P.dark ? 0.5 : 0.35) +
    ball(`${p}pd`, P.green, 0.35, 0.4) +
    body(`${p}lo`, P.pink, 0.45, 0.3, 0.4, 1) +
    body(`${p}lc`, tint(P.pink, 0.25), 0.55, 0.2, 0.4, 1) +
    `<clipPath id="${p}ckg"><path d="${KOI_BODY}"/></clipPath><clipPath id="${p}ckr"><path d="${KOI_BODY}"/></clipPath>` +
    `</defs>`;

  return (
    defs +
    bg.body +
    stars +
    `<circle cx="500" cy="96" r="104" fill="url(#${p}mh)"/>` +
    `<circle cx="500" cy="96" r="48" fill="url(#${p}mo)" stroke="${shade(moon, 0.2)}" stroke-width="1.6"/>` +
    `<path d="M0 238C60 214 140 212 220 228C300 206 360 212 420 228L640 226V250H0Z" fill="${shore}" fill-opacity="${P.dark ? 0.9 : 0.35}"/>` +
    `<path d="M0 240C160 230 480 230 640 240V400H0Z" fill="url(#${p}wt)"/>` +
    `<path d="M470 262h60M480 278h40M490 294h20" ${white(0.3, 0.8)} stroke-width="3" stroke-linecap="round"/>` +
    `<path d="M406 244C412 336 568 336 574 244" fill="none" stroke="${P.red}" stroke-opacity="${P.dark ? 0.4 : 0.45}" stroke-width="12" stroke-linecap="round"/>` +
    `<path d="M372 210C386 110 594 110 608 210" fill="none" stroke="${edge(P.red, P)}" stroke-width="4" stroke-linecap="round"/>` +
    `<path d="M384 184V200M410 148V166M444 128V146M490 122V140M536 128V146M570 148V166M596 184V200" stroke="${edge(P.red, P)}" stroke-width="4" stroke-linecap="round"/>` +
    `<path d="M364 244C376 134 604 134 616 244H574C564 170 416 170 406 244Z" fill="url(#${p}br)" stroke="${edge(P.red, P)}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M378 222C392 150 588 150 602 222" fill="none" stroke="${tint(P.red, 0.45)}" stroke-opacity=".6" stroke-width="2.4" stroke-linecap="round"/>` +
    gloss(420, 172, 26, 5, -28, 0.4) +
    `<path d="M120 300q30 -8 60 0M300 262q40 -8 80 0M60 376q24 -6 48 0" fill="none" ${white(0.2, 0.6)} stroke-width="2" stroke-linecap="round"/>` +
    `<ellipse cx="380" cy="332" rx="96" ry="30" fill="none" ${white(0.14, 0.45)} stroke-width="2"/>` +
    pad(572, 352, 52, -6) +
    pad(262, 380, 38, 6) +
    pad(608, 290, 30, -10) +
    flower(578, 346, 1.2) +
    flower(262, 376, 0.8) +
    koi(336, 316, -12, 1.3, gold, P.red, 'kg') +
    koi(478, 356, 10, -1.15, P.red, gold, 'kr') +
    `<path d="M256 26C300 50 380 50 424 22" fill="none" stroke="${P.dark ? tint(P.ink, 0.2) : shade(P.ink, 0.1)}" stroke-opacity=".45" stroke-width="2"/>` +
    lantern(290, 106, 0.86) +
    lantern(348, 98, 0.7) +
    lantern(400, 82, 0.56)
  );
}
